import React, { useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Icon } from "components";
import type { OrderCodePart as OrderCodePartType } from "stores/product";
import {
  useSetActivePartKey,
  useActivePartKey,
  useProductOutput,
  useActiveTab,
  useSetActiveTab,
  ConfiguratorTabs,
} from "stores/product";

import { useTranslate } from "hooks";
import s from "./ProductConfiguratorLeftSidebar.module.scss";
import type { MenuItem } from "./variables";

export const ProductConfiguratorLeftSidebarItem = Scrivito.connect(
  ({ item }: { item: MenuItem }) => {
    const translate = useTranslate();
    const activeTab = useActiveTab();
    const setActiveTab = useSetActiveTab();
    const productOutput = useProductOutput();
    const setActivePartKey = useSetActivePartKey();
    const activePartKey = useActivePartKey();
    const [showParts, setShowParts] = useState(true);
    const parts = productOutput?.product?.orderCode?.parts || [];
    const hasParts = parts.length > 0;
    const isActiveTab = activeTab === item.tab;

    const activateProductsNavigationItem = useCallback(() => {
      window.scrollTo(0, 0);
      if (hasParts) {
        setShowParts(isActiveTab ? !showParts : true);
      }

      setActiveTab(item.tab);
      setActivePartKey(null);
    }, [
      hasParts,
      isActiveTab,
      item.tab,
      setActivePartKey,
      setActiveTab,
      showParts,
    ]);

    const handleClickPart = useCallback(
      (part: OrderCodePartType) => {
        setActivePartKey(part.key);
        const partElement = document.getElementById(`part-${part.key}`);
        partElement?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      },
      [setActivePartKey],
    );

    return (
      <div>
        <button
          onClick={activateProductsNavigationItem}
          className={cx(
            "w-100 border-0 position-relative d-flex justify-content-between align-items-center m-0 fs-18 p-3_5 text-nowrap cursor-pointer text-truncate",
            s.productsNavigationItem,
            s.backgroundTest,
            {
              [s.productsNavigationItemActive]: isActiveTab,
            },
          )}
        >
          {translate(item.title)}
          {item.tab === ConfiguratorTabs.PRODUCT_SPECIFICATION && hasParts && (
            <Icon
              iconName={
                isActiveTab && showParts
                  ? "chevron-up-regular"
                  : "chevron-down-regular"
              }
              className="icon-32"
            />
          )}
        </button>
        {item.tab === ConfiguratorTabs.PRODUCT_SPECIFICATION &&
          hasParts &&
          isActiveTab &&
          showParts &&
          parts
            .filter(
              (part) =>
                part?.state !== 2 &&
                part?.state !== 4 &&
                (part?.state !== 3 ||
                  (part?.state === 3 && part?.presentation)),
            )
            .map((part, index) => (
              <div className="px-4" key={index}>
                <button
                  className={cx(
                    "d-flex w-100 justify-content-between px-2 py-3 cursor-pointer text-truncate fs-md",
                    s.part,
                    {
                      [s.partActive]: activePartKey === part?.key,
                    },
                  )}
                  onClick={() => handleClickPart(part)}
                  title={part?.title ?? ""}
                >
                  {part?.title ?? ""}
                </button>
              </div>
            ))}
      </div>
    );
  },
);
